import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  CircularProgress,
  Alert,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Axios from "../../Common/api/axiosConfig";
import AnnouncementBar from "./AnnouncementBar";
import StoreHeader from "./Header";
import StoreHero from "./Hero";
import TrustBadges from "./TrustBadges";
import CategoryGrid from "./CategoryGrid";
import StoreProductPage from "./Product";
import BrandNote from "./BrandNote";
import StoreFooter from "./Footer";
import { Product } from "../manage-products/manageProducts";
import { getStoreTheme } from "./themes/storeThemes";
import { uploadLink } from "../../utils/constants";
import { getWhatsAppUrl } from "../../utils/whatsappHelper";
import { enrichProductWithAmazonLink } from "../../utils/amazonHelper";
import { enrichProductWithWarranty } from "../../utils/warrantyHelper";

const Store: React.FC = () => {
  const { url: paramUrl } = useParams<{ url: string }>();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [storeData, setStoreData] = useState<any | null>(null);

  // Search & Category Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const themeTokens = getStoreTheme(storeData?.theme);

  // Determine store lookup key: from URL param or current domain
  const getLookupUrl = (): string => {
    if (paramUrl) return paramUrl;
    const hostname = window.location.hostname.toLowerCase().replace(/^www\./, "");
    if (
      hostname !== "app.therudrix.in" &&
      !hostname.startsWith("admin.") &&
      hostname !== "localhost" &&
      hostname !== "127.0.0.1"
    ) {
      return hostname;
    }
    return "shop";
  };

  const activeUrl = getLookupUrl();

  const fetchStoreAndProducts = async () => {
    try {
      setLoading(true);
      let res = await Axios.get(`/store/products/${activeUrl}`);
      if (!res.data?.success && activeUrl !== "shop") {
        res = await Axios.get(`/store/products/shop`);
      }

      if (res.data?.success && res.data.data?.storeData) {
        const { storeData: fetchedStoreData, productData } = res.data.data;
        setStoreData(fetchedStoreData);
        const rawProducts = productData || [];
        setProducts(
          rawProducts.map((p: any) =>
            enrichProductWithWarranty(enrichProductWithAmazonLink(p))
          )
        );
      } else {
        setError("Store not found.");
      }
    } catch (err: any) {
      if (activeUrl !== "shop") {
        try {
          const fallbackRes = await Axios.get(`/store/products/shop`);
          if (fallbackRes.data?.success && fallbackRes.data.data?.storeData) {
            setStoreData(fallbackRes.data.data.storeData);
            const fallbackProducts = fallbackRes.data.data.productData || [];
            setProducts(
              fallbackProducts.map((p: any) =>
                enrichProductWithWarranty(enrichProductWithAmazonLink(p))
              )
            );
            setLoading(false);
            return;
          }
        } catch (_) {}
      }
      const status = err.response?.status;
      setError(
        status === 404
          ? "Store not found. Please check the URL."
          : status === 500
          ? "Server error. Please try again later."
          : "Failed to load store. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOrder = async (product: Product) => {
    const isInStock = product.inStock !== false && (product as any).in_stock !== false;
    if (!isInStock) return;

    const amazonUrl = (product.amazonLink || product.amazon_link || "").trim();
    // If Amazon link is configured, direct the customer straight to Amazon
    if (amazonUrl) {
      window.open(amazonUrl, "_blank", "noopener,noreferrer");
      try {
        await Axios.patch(`/store/order/${product._id}`);
      } catch (_) {}
      return;
    }

    if (!storeData) return;

    try {
      const res = await Axios.patch(`/store/order/${product._id}`);
      if (res.data?.success) {
        const message = `Hi! I'm interested in ${product.name} (₹${product.price}) from ${storeData.name}.`;
        const waUrl = getWhatsAppUrl(storeData.number, message);
        if (waUrl) window.open(waUrl, "_blank");
      }
    } catch (err) {
      console.error("Error creating order:", err);
      // Fallback open WhatsApp directly
      const message = `Hi! I'm interested in ${product.name} (₹${product.price}) from ${storeData.name}.`;
      const waUrl = getWhatsAppUrl(storeData.number, message);
      if (waUrl) window.open(waUrl, "_blank");
    }
  };

  const handleResetFilter = () => {
    setSearchQuery("");
    setSelectedCategory("All");
  };

  useEffect(() => {
    fetchStoreAndProducts();
  }, [activeUrl]);

  // Dynamically update document title and favicon based on store settings
  useEffect(() => {
    if (storeData) {
      if (storeData.name) {
        document.title = `${storeData.name} - Official Online Store`;
      }
      const rawFav =
        storeData.header?.favicon ||
        storeData.favicon ||
        storeData.header?.logo ||
        (storeData._id ? localStorage.getItem(`rudrix_store_favicon_${storeData._id}`) : "");
      if (rawFav) {
        const fullFavUrl =
          rawFav.startsWith("http") || rawFav.startsWith("data:")
            ? rawFav
            : `${uploadLink}${rawFav.replace(/^\//, "")}`;

        let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement("link");
          link.rel = "shortcut icon";
          document.getElementsByTagName("head")[0].appendChild(link);
        }
        link.href = fullFavUrl;
      }
    }
  }, [storeData]);

  // Loading
  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: themeTokens.bgMain,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={56} sx={{ color: themeTokens.accent, mb: 2 }} />
          <Typography variant="h6" sx={{ color: themeTokens.textSecondary, fontFamily: themeTokens.fontSerif }}>
            Loading store experience...
          </Typography>
        </Box>
      </Box>
    );

  // Error
  if (error || !storeData)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: themeTokens.bgMain,
          px: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            {error || "Store not found"}
          </Typography>
          <Typography variant="body2">
            The store you're looking for doesn't exist or has been removed.
          </Typography>
        </Alert>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: themeTokens.bgMain,
        color: themeTokens.textPrimary,
        width: "100%",
        overflowX: "hidden",
        fontFamily: themeTokens.fontSans,
      }}
    >
      {/* 1. Top Announcement Bar */}
      <AnnouncementBar
        announcementData={storeData.announcement}
        themeTokens={themeTokens}
      />

      {/* 2. Main Sticky Header with Category Sub-navigation */}
      <StoreHeader
        storeData={storeData}
        themeTokens={themeTokens}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />

      {/* 3. Hero Section matching Google Stitch */}
      <StoreHero
        storeData={storeData}
        products={products}
        themeTokens={themeTokens}
      />

      {/* 4. Value Proposition / 4 Trust Badges */}
      <TrustBadges
        trustData={storeData.trust_badges}
        themeTokens={themeTokens}
      />

      {/* 5. Shop by Category Quick Grid */}
      <CategoryGrid
        storeData={storeData}
        onSelectCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        themeTokens={themeTokens}
      />

      {/* 6. Products Grid Section & Cards */}
      <StoreProductPage
        products={products}
        handleOrder={handleOrder}
        storeData={storeData}
        themeTokens={themeTokens}
        searchQuery={searchQuery}
        selectedCategory={selectedCategory}
        onResetFilter={handleResetFilter}
      />

      {/* 7. Brand Appreciation Note Banner */}
      <BrandNote
        noteData={storeData.brand_note}
        storeName={storeData.name}
        themeTokens={themeTokens}
      />

      {/* 8. Luxury Dark Footer */}
      <StoreFooter
        storeData={storeData}
        themeTokens={themeTokens}
        onSelectCategory={setSelectedCategory}
      />

      {/* 💬 Floating WhatsApp Button */}
      {storeData?.number && (
        <IconButton
          onClick={() => {
            const waUrl = getWhatsAppUrl(
              storeData.number,
              `Hi ${storeData.name}, I have a question`
            );
            if (waUrl) window.open(waUrl, "_blank");
          }}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            bgcolor: "#22c55e",
            color: "white",
            width: 56,
            height: 56,
            boxShadow: "0 4px 16px rgba(34,197,94,0.4)",
            "&:hover": { bgcolor: "#16a34a", transform: "scale(1.08)" },
            transition: "all 0.25s ease",
            zIndex: 1000,
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 32, color: "#fff" }} />
        </IconButton>
      )}

      {/* Snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Box>
  );
};

export default Store;
